import { readAllRows } from "@/lib/read-all-rows";
import { operation } from "@/lib/api-client";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Palette, Loader2, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { ContextualHelpLink } from "@/components/help/contextual-help-link";

export const Route = createFileRoute("/design/new")({
  head: () => ({
    meta: [
      { title: "New Design — AskBell" },
      { name: "description", content: "Start a new agentic design with a brief and a Design System." },
    ],
  }),
  component: NewDesignPage,
});

const SUGGESTIONS = [
  "A 10-slide investor update covering Q1 wins, pipeline, and the next hire.",
  "A client-ready research summary deck on Gen-Z financial behavior, 12 slides, with a recommendations section.",
  "A one-pager pitching our customer experience benchmarking offering to a regional bank.",
];

function NewDesignPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [systemId, setSystemId] = useState<string>("none");
  const [systems, setSystems] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [systemsLoading, setSystemsLoading] = useState(true);
  const [systemsError, setSystemsError] = useState<string | null>(null);
  const [reloadSystems, setReloadSystems] = useState(0);
  const submission = useRef<{ signature: string; requestId: string } | null>(null);
  const submitting = useRef(false);

  useEffect(() => {
    let cancelled=false;
    if(!user){setSystems([]);setSystemsLoading(false);return;}
    setSystemsLoading(true);setSystemsError(null);
    void readAllRows<{id:string;name:string}>((from,to)=>awsClient.from("design_systems").select("id, name").order("name").order("id",{ascending:true}).range(from,to),()=>!cancelled)
      .then(({data,error})=>{
        if(cancelled)return;
        if(error)setSystemsError(error.message);else setSystems(data);
      }).catch(error=>{if(!cancelled)setSystemsError(error instanceof Error?error.message:"Could not load design systems.");})
      .finally(()=>{if(!cancelled)setSystemsLoading(false);});
    return()=>{cancelled=true;};
  },[user,reloadSystems]);

  const canCreate = !!user && brief.trim().length >= 10 && !busy && !systemsLoading && !systemsError;

  const onCreate = async () => {
    if (!user || !canCreate || submitting.current) return;
    const details = {
      title: title.trim(),
      brief: brief.trim(),
      designSystemId: systemId === "none" ? null : systemId,
    };
    const signature = JSON.stringify(details);
    if (submission.current?.signature !== signature) {
      submission.current = { signature, requestId: crypto.randomUUID() };
    }
    submitting.current = true;
    setBusy(true);
    try {
      const data = await operation<typeof details & { requestId: string }, { id: string }>(
        "design_create", { ...details, requestId: submission.current.requestId },
      );
      await navigate({ to: "/design/$designId", params: { designId: data.id } });
    } catch (error) {
      toast.error("Couldn't create design", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl px-6 py-8">
          <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 gap-1.5">
            <Link to="/design">
              <ArrowLeft className="h-4 w-4" />
              All designs
            </Link>
          </Button>

          <div className="flex items-center gap-2 mb-2">
            <Palette className="h-5 w-5 text-primary" />
            <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
              New design
            </h1>
          </div>
          <p className="text-ui-base text-muted-foreground mb-2">
            Describe what you want. The agent will ask clarifying questions if
            the brief is ambiguous, then plan and draft a deck.
          </p>
          <ContextualHelpLink topic="design" label="Help" className="mb-6" />

          <div className="space-y-5">
            <div>
              <Label htmlFor="title" className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                Title
              </Label>
              <Input
                disabled={busy}
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Q1 board update"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="brief" className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                Brief
              </Label>
              <Textarea
                disabled={busy}
                id="brief"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="What should the deck or document cover? Audience, length, must-include points…"
                className="mt-1.5 min-h-[140px]"
                data-tour-design-brief=""
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    disabled={busy}
                    key={s}
                    onClick={() => setBrief(s)}
                    className="text-ui-xs px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                  >
                    <Sparkles className="h-3 w-3 inline mr-1" />
                    {s.slice(0, 60)}{s.length > 60 ? "…" : ""}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                Design System
              </Label>
              {systemsLoading && <p role="status" className="mt-2 text-ui-sm text-muted-foreground">Loading design systems…</p>}
              {systemsError && <div role="alert" className="mt-2 rounded-ui-md border border-destructive/30 p-3 text-ui-base"><p>{systemsError}</p><Button variant="outline" size="sm" className="mt-2" onClick={()=>setReloadSystems(value=>value+1)}>Try again</Button></div>}
              <div className="mt-1.5 flex flex-wrap gap-2">
                <Select value={systemId} onValueChange={setSystemId} disabled={busy || systemsLoading || Boolean(systemsError)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Pick a design system" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No system (plain professional default)</SelectItem>
                    {systems.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button asChild variant="outline" size="sm">
                  <Link to="/design/systems">Manage</Link>
                </Button>
              </div>
              <p className="text-ui-xs text-muted-foreground mt-1.5">
                The agent will honor the palette, fonts, voice, and analysis
                style from the selected system.
              </p>
            </div>

            <div className="pt-2">
              <Button onClick={onCreate} disabled={!canCreate} className="gap-1.5">
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Start designing
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
